import {
  AlertTriangle,
  BrainCircuit,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileSearch,
  FileUp,
  History,
  Info,
  LayoutDashboard,
  LoaderCircle,
  LockKeyhole,
  Search,
  ShieldCheck,
  UploadCloud,
  WalletCards,
  X,
} from "lucide-react";
import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import type { PortfolioBookKindV2 } from "@tradvera/contracts/portfolio-v2";
import type {
  MyPortfolioHoldingReadModel,
  MyPortfolioNextReadModel,
} from "../contracts.js";
import { HistoryScreen, StyleScreen } from "./MyPortfolioInsightScreens.js";
import {
  Allocation,
  bytes,
  dateTime,
  Detail,
  duration,
  MetricCard,
  MiniMetric,
  money,
  PanelHead,
  plain,
  qualityLabel,
  Result,
  ScreenIntro,
  StateScreen,
  Stock,
  toneOf,
  tradeById,
  TradeOutcome,
} from "./MyPortfolioNextPrimitives.js";
import "./my-portfolio-next.css";

export type PortfolioPage = "summary" | "holdings" | "history" | "style" | "upload";
type SelectableBookKind = Extract<PortfolioBookKindV2, "OWNED_PRIVATE" | "PAPER">;

export interface MyPortfolioUploadFileReview {
  importId: string;
  originalName: string;
  role: "POSITIONS" | "CASH" | "ORDER_HISTORY" | "OPEN_ORDERS" | "ACTIVITY_LOG" | "OTHER";
  state: string;
  recordCount: number;
  actionableRecords: number;
  exceptionFields: number;
}

export interface MyPortfolioUploadViewState {
  connection: "UNAVAILABLE" | "READY";
  busy: boolean;
  busyLabel?: string | null;
  errorCode?: string | null;
  files: MyPortfolioUploadFileReview[];
  canApprove: boolean;
  onUploadFiles(files: File[]): Promise<{ ok: true } | { ok: false; errorCode: string }>;
  onApprove(): Promise<boolean>;
}

export interface MyPortfolioNextProps {
  models?: Partial<Record<SelectableBookKind, MyPortfolioNextReadModel | null>>;
  initialBookKind?: SelectableBookKind;
  selectedBookKind?: SelectableBookKind;
  initialPage?: PortfolioPage;
  loadingBookKind?: SelectableBookKind | null;
  errorByBook?: Partial<Record<SelectableBookKind, string | null>>;
  onBookChange?: (bookKind: SelectableBookKind) => void;
  upload?: MyPortfolioUploadViewState;
}

const pageMeta: Record<PortfolioPage, { ar: string; en: string; icon: ReactNode }> = {
  summary: { ar: "الملخص", en: "الصورة الكاملة", icon: <LayoutDashboard /> },
  holdings: { ar: "الأسهم والمراكز", en: "المراكز المفتوحة", icon: <WalletCards /> },
  history: { ar: "تاريخ الصفقات", en: "الدخول والخروج", icon: <History /> },
  style: { ar: "تحليل أسلوبك", en: "قياس السلوك", icon: <BrainCircuit /> },
  upload: { ar: "رفع كشف البروكر", en: "قراءة ذكية للملفات", icon: <FileUp /> },
};

export function MyPortfolioNext({
  models = {},
  initialBookKind = "PAPER",
  selectedBookKind,
  initialPage = "summary",
  loadingBookKind = null,
  errorByBook = {},
  onBookChange,
  upload,
}: MyPortfolioNextProps) {
  const [page, setPage] = useState<PortfolioPage>(initialPage);
  const [internalBookKind, setInternalBookKind] = useState<SelectableBookKind>(initialBookKind);
  const bookKind = selectedBookKind ?? internalBookKind;
  const model = models[bookKind] ?? null;
  const error = errorByBook[bookKind] ?? null;
  if (model && (model.identity.bookKind !== bookKind || !model.identity.bookId)) {
    throw new Error("MY_PORTFOLIO_NEXT_UI_BOOK_SCOPE_MISMATCH");
  }
  const switchBook = (next: SelectableBookKind) => {
    if (next === bookKind) return;
    setInternalBookKind(next);
    setPage("summary");
    onBookChange?.(next);
  };
  return <main className="portfolio-x-preview portfolio-x-approved" dir="rtl">
    <section className={`portfolio-x-card ${bookKind === "PAPER" ? "is-paper" : "is-owned"}`}>
      <header className="portfolio-x-approved-head">
        <div className="portfolio-x-approved-brand" dir="ltr" aria-label="Tradvera">
          <span className="portfolio-x-brand-mark">T</span>
          <span><b>TRADVERA</b><small>LEARN · DECIDE · TRADE</small></span>
        </div>
        <div className="portfolio-x-approved-controls">
          <span className="portfolio-x-approved-badge"><i /> حقيقة مالية مرتبطة بالمصدر</span>
          <BookSwitcher bookKind={bookKind} onChange={switchBook} />
        </div>
      </header>

      <section className="portfolio-x-approved-hero">
        <span>MY PORTFOLIO · ONE CARD · FIVE VIEWS</span>
        <h1>كل ما يهمك عن محفظتك في بطاقة واحدة.</h1>
        <p>ماذا تملك الآن؟ ماذا ربحت أو خسرت؟ كيف كانت صفقاتك؟ وهل توجد أدلة كافية لقياس تحسن أسلوبك؟ كل رقم يعود إلى مصدره، والمفقود يبقى غير معلوم.</p>
        <div className="portfolio-x-sync-card">
          <b>{bookKind === "PAPER" ? "محفظة Paper" : "المحفظة الحقيقية"}</b>
          <span>{model ? `آخر حقيقة في المصدر: ${dateTime(model.freshness.asOf)}` : "لم تُقبل حزمة مصدر لهذا الدفتر بعد"}</span>
        </div>
      </section>

      <nav className="portfolio-x-tabs" aria-label="صفحات محفظتي الجديدة">
        {(Object.keys(pageMeta) as PortfolioPage[]).map((id) => <button key={id} type="button"
          className={page === id ? "is-active" : ""} aria-current={page === id ? "page" : undefined}
          onClick={() => setPage(id)}>
          <i>{pageMeta[id].icon}</i><span><b>{pageMeta[id].ar}</b><small>{pageMeta[id].en}</small></span>
        </button>)}
      </nav>

      <section className="portfolio-x-page" aria-live="polite">
        {page === "upload"
          ? <UploadScreen bookKind={bookKind} model={model} upload={upload} />
          : loadingBookKind === bookKind
            ? <StateScreen icon={<LoaderCircle />} title="جارٍ بناء حقيقة المحفظة" text="يتم التحقق من الملفات والتصنيف والمطابقة قبل إظهار أي رقم." />
            : error
              ? <StateScreen icon={<AlertTriangle />} title="تعذر تحميل حقيقة المحفظة" text={`رمز التشخيص: ${error}`} />
              : !model
                ? <EmptyScreen onUpload={() => setPage("upload")} />
                : <ReadyScreen page={page} model={model} />}
      </section>

      <footer className="portfolio-x-card-foot">
        <span><LockKeyhole /> الحساب والمطابقة في النطاق الحتمي؛ الواجهة تنسّق النتيجة فقط</span>
        <span>Owned وPaper دفتران منفصلان في القراءة والذاكرة والتخزين</span>
      </footer>
    </section>
  </main>;
}

function ReadyScreen({ page, model }: { page: Exclude<PortfolioPage, "upload">; model: MyPortfolioNextReadModel }) {
  return <>
    <QualityReceipt model={model} />
    {page === "summary" && <SummaryScreen model={model} />}
    {page === "holdings" && <HoldingsScreen model={model} />}
    {page === "history" && <HistoryScreen model={model} />}
    {page === "style" && <StyleScreen model={model} />}
  </>;
}

function BookSwitcher({ bookKind, onChange }: {
  bookKind: SelectableBookKind; onChange(bookKind: SelectableBookKind): void;
}) {
  return <div className="portfolio-x-book-switch" aria-label="اختر دفتر المحفظة">
    <button type="button" className={bookKind === "OWNED_PRIVATE" ? "is-active" : ""}
      onClick={() => onChange("OWNED_PRIVATE")}><span><b>المحفظة الحقيقية</b></span></button>
    <button type="button" className={bookKind === "PAPER" ? "is-active" : ""}
      onClick={() => onChange("PAPER")}><span><b>Paper</b></span></button>
  </div>;
}

function QualityReceipt({ model }: { model: MyPortfolioNextReadModel }) {
  const receipt = model.dataQuality;
  const tone = receipt.state === "BLOCKED" ? "is-blocked"
    : receipt.state === "READY_WITH_WARNINGS" || receipt.state === "REVIEW_REQUIRED" ? "is-warning" : "";
  return <div className={`portfolio-next-quality ${tone}`}>
    <span><strong>{qualityLabel(receipt.state)}</strong> · {receipt.sourceFileCount} ملفات · {receipt.sourceRecordCount} سجلًا</span>
    <span>{receipt.unknownFeeRecordCount > 0 ? `رسوم غير معلومة في ${receipt.unknownFeeRecordCount} تنفيذًا` : "الرسوم مكتملة"}</span>
  </div>;
}

function SummaryScreen({ model }: { model: MyPortfolioNextReadModel }) {
  const summary = model.summary;
  const best = tradeById(model, model.behavior.bestTradeId);
  const worst = tradeById(model, model.behavior.worstTradeId);
  return <div className="portfolio-x-screen portfolio-x-approved-summary">
    <ScreenIntro eyebrow={`${model.identity.bookKind === "PAPER" ? "PAPER" : "OWNED"} · ${model.identity.currency}`}
      title="ملخص المحفظة" description="الصورة المالية الحالية ونتائج الصفقات المقبولة من المصدر." />
    <div className="portfolio-x-approved-kpis">
      <MetricCard label="قيمة المحفظة الحالية" metric={summary.currentPortfolioValue} currency={model.identity.currency} />
      <MetricCard label="رصيد الحساب" metric={summary.accountBalance} currency={model.identity.currency} />
      <MetricCard label="إجمالي تكلفة المراكز" metric={summary.totalPurchaseValue} currency={model.identity.currency} />
      <MetricCard label="القيمة السوقية للمراكز" metric={summary.totalMarketValue} currency={model.identity.currency} />
      <MetricCard label="إجمالي الربح / الخسارة" metric={summary.totalPnl} currency={model.identity.currency}
        tone={toneOf(summary.totalPnl.value)} />
      <MetricCard label="المراكز المفتوحة" metric={summary.positionsCount} />
    </div>
    <section className="portfolio-x-approved-chart-card">
      <PanelHead title="تطور قيمة المحفظة" subtitle="يظهر بعد توفر لقطات زمنية متصالحة" />
      <div className="portfolio-next-empty-chart"><span>الكشف الحالي لقطة واحدة؛ لن نرسم مسارًا زمنيًا وهميًا.</span></div>
    </section>
    <div className="portfolio-x-approved-summary-lower">
      <section className="portfolio-x-approved-chart-card">
        <PanelHead title="توزيع المراكز" subtitle="حسب القيمة السوقية الحالية" />
        <Allocation model={model} />
      </section>
      <section className="portfolio-x-approved-chart-card portfolio-x-approved-best-worst">
        <PanelHead title="أفضل وأسوأ صفقة مغلقة" subtitle={`${summary.closedTradesCount.value ?? 0} صفقة مقبولة`} />
        <div>
          <TradeOutcome label="أفضل صفقة" trade={best} currency={model.identity.currency} tone="is-best" />
          <TradeOutcome label="أسوأ صفقة" trade={worst} currency={model.identity.currency} tone="is-worst" />
        </div>
      </section>
    </div>
    <div className="portfolio-next-summary-note"><Info />
      <span>قيمة TradingView Paper هنا تتبع تعريف المصدر: رصيد الحساب + الربح غير المحقق. الرسوم الخالية لا تُعامل كصفر، والتحليل النفسي لا يصدر دون خطة وقرار سابقين.</span>
    </div>
  </div>;
}

function HoldingsScreen({ model }: { model: MyPortfolioNextReadModel }) {
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  const holdings = model.holdings.filter((holding) => `${holding.symbol} ${holding.instrumentId}`
    .toLowerCase().includes(query.trim().toLowerCase()));
  return <div className="portfolio-x-screen">
    <ScreenIntro eyebrow="CURRENT POSITIONS · SOURCE SNAPSHOT" title="الأسهم والمراكز المملوكة الآن"
      description="كل صف يمثل مركزًا مفتوحًا في أحدث لقطة مراكز مقبولة." />
    <div className="portfolio-x-mini-totals">
      <MiniMetric label="عدد المراكز" metric={model.summary.positionsCount} />
      <MiniMetric label="التكلفة" metric={model.summary.totalPurchaseValue} currency={model.identity.currency} />
      <MiniMetric label="القيمة الحالية" metric={model.summary.totalMarketValue} currency={model.identity.currency} />
      <MiniMetric label="غير المحقق" metric={model.summary.unrealizedPnl} currency={model.identity.currency} />
      <MiniMetric label="وقت المصدر" text={dateTime(model.freshness.asOf)} />
    </div>
    <section className="portfolio-x-panel portfolio-x-table-panel">
      <div className="portfolio-x-table-toolbar"><div><Search /><input value={query} onChange={(event) => setQuery(event.target.value)}
        placeholder="ابحث بالرمز" /></div><span><ShieldCheck /> {holdings.length} مركزًا مطابقًا</span></div>
      <div className="portfolio-x-table-scroll"><table className="portfolio-x-table"><thead><tr>
        <th>السهم / الأصل</th><th>الكمية</th><th>متوسط الشراء</th><th>السعر الحالي</th><th>تكلفة الشراء</th>
        <th>القيمة الحالية</th><th>الربح / الخسارة</th><th>الجودة</th><th />
      </tr></thead><tbody>{holdings.map((holding) => <HoldingRows key={holding.holdingId} holding={holding}
        currency={model.identity.currency} expanded={expanded === holding.holdingId}
        onToggle={() => setExpanded(expanded === holding.holdingId ? null : holding.holdingId)} />)}</tbody></table></div>
    </section>
  </div>;
}

function HoldingRows({ holding, currency, expanded, onToggle }: {
  holding: MyPortfolioHoldingReadModel; currency: string; expanded: boolean; onToggle(): void;
}) {
  return <>
    <tr className={expanded ? "is-expanded" : ""}>
      <td><Stock symbol={holding.symbol} subtitle={holding.assetClass.value ?? "غير مصنف"} /></td>
      <td>{plain(holding.quantity)}</td><td>{money(holding.averagePurchasePrice, currency)}</td>
      <td>{money(holding.currentPrice, currency)}</td><td>{money(holding.totalPurchaseValue, currency)}</td>
      <td>{money(holding.currentValue, currency)}</td>
      <td><Result metric={holding.unrealizedPnl} pct={holding.unrealizedPnlPct} currency={currency} /></td>
      <td><span className="portfolio-x-quality">{plain(holding.positionQuality)}</span></td>
      <td><button type="button" className="portfolio-x-row-toggle" onClick={onToggle}>{expanded ? <ChevronUp /> : <ChevronDown />}</button></td>
    </tr>
    {expanded && <tr className="portfolio-x-detail-row"><td colSpan={9}><div className="portfolio-x-holding-detail">
      <Detail label="أول شراء" value={plain(holding.firstBuyAt)} /><Detail label="آخر شراء" value={plain(holding.lastBuyAt)} />
      <Detail label="مدة الاحتفاظ" value={duration(holding.holdingDurationMs.value)} /><Detail label="القطاع" value={plain(holding.sector)} />
      <Detail label="المصدر" value={holding.quantity.sourceIds.join("، ") || "غير متاح"} />
      <div className="portfolio-x-detail-note"><Info /><span><b>حالة الدليل</b><small>تواريخ اللوت والقطاع والجودة لا توجد في كشف المراكز الحالي؛ لذلك لم تُستنتج أو تُملأ بقيم افتراضية.</small></span></div>
    </div></td></tr>}
  </>;
}

function UploadScreen({ bookKind, model, upload }: {
  bookKind: SelectableBookKind; model: MyPortfolioNextReadModel | null;
  upload?: MyPortfolioUploadViewState;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [attempting, setAttempting] = useState(false);
  const [localUploadError, setLocalUploadError] = useState<string | null>(null);
  const select = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalUploadError(null); setFiles(Array.from(event.target.files ?? []));
  };
  const clearFiles = () => {
    setFiles([]); setLocalUploadError(null);
    if (inputRef.current) inputRef.current.value = "";
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setLocalUploadError(null);
    if (!files.length) { setLocalUploadError("STATEMENT_IMPORT_FILE_COUNT_INVALID"); return; }
    if (!upload || upload.connection !== "READY") {
      setLocalUploadError("STATEMENT_IMPORT_CONNECTION_UNAVAILABLE"); return;
    }
    setAttempting(true);
    try {
      const result = await upload.onUploadFiles(files);
      if (result.ok) clearFiles();
      else setLocalUploadError(result.errorCode);
    } catch (error) {
      setLocalUploadError(error instanceof Error && error.message
        ? error.message : "STATEMENT_IMPORT_REQUEST_FAILED");
    } finally {
      setAttempting(false);
    }
  };
  const staged = upload?.files ?? [];
  const step = staged.length ? (upload?.busy ? 3 : 4) : upload?.busy ? 2 : 1;
  return <div className="portfolio-x-screen">
    <ScreenIntro eyebrow="CONTENT-FIRST · SHA-256 · OWNER REVIEW" title="رفع كشف البروكر"
      description="ارفع حزمة TradingView كاملة؛ يتم تصنيف كل ملف من محتواه ثم عرض تسكين واحد للمراجعة قبل أي اعتماد." />
    <div className="portfolio-x-upload-steps"><span className={step > 1 ? "is-done" : "is-active"}><i>01</i><b>قراءة المحتوى</b><small>لا ثقة في الاسم</small></span>
      <ChevronDown /><span className={step > 2 ? "is-done" : step === 2 ? "is-active" : ""}><i>02</i><b>تصنيف الملفات</b><small>خمسة أدوار</small></span><ChevronDown />
      <span className={step > 3 ? "is-done" : step === 3 ? "is-active" : ""}><i>03</i><b>مطابقة التنفيذات</b><small>منع التكرار</small></span><ChevronDown />
      <span className={step === 4 ? "is-active" : ""}><i>04</i><b>مراجعة واحدة</b><small>Receipt للمالك</small></span></div>
    <div className="portfolio-x-upload-layout">
      <section className="portfolio-x-panel portfolio-x-drop-panel">
        <div className="portfolio-x-local-only"><ShieldCheck /><span><b>الدفتر المستهدف: {bookKind === "PAPER" ? "Paper" : "المحفظة الحقيقية"}</b>
          <small>لا يمكن نقل نتيجة الحزمة إلى الدفتر الآخر.</small></span></div>
        <div className="portfolio-x-dropzone"><span><UploadCloud /></span><h3>اختر ملفات الكشف الخمسة</h3>
          <p>الإصدار الأول يدعم حزمة TradingView CSV: المراكز، الأوامر الحالية، سجل الأوامر، سجل الرصيد، وسجل النشاط.</p>
          <div><b>CSV</b><b>حتى 100 ملف</b><b>SHA-256 لكل ملف</b></div>
          <input ref={inputRef} className="portfolio-next-dropzone-input" type="file" accept=".csv,text/csv" multiple onChange={select} />
          <button className="portfolio-x-dropzone-demo" type="button" disabled={upload?.connection !== "READY" || upload.busy}
            onClick={() => inputRef.current?.click()}>اختيار الملفات</button>
          <span className="portfolio-x-dropzone-note">الملف المشفّر وأثره يرفعان للفحص؛ لا تُعتمد الملاحظات قبل المراجعة وتأكيد المالك.</span></div>
        {files.length > 0 && <form className="portfolio-x-local-files" onSubmit={submit}><header><span><FileSearch />{files.length} ملفات محلية</span>
          <button type="button" onClick={clearFiles}><X />مسح</button></header><ul>{files.map((file) => <li key={`${file.name}:${file.size}`}>
            <FileUp /><span><b>{file.name}</b><small>{bytes(file.size)}</small></span><em>جاهز للقراءة</em></li>)}</ul>
          {(attempting || upload?.busy) && <div className="portfolio-next-upload-feedback is-progress" role="status" aria-live="polite">
            <LoaderCircle /><span><b>تم استلام الطلب</b><small>{upload?.busyLabel ?? "جارٍ بدء فحص الحزمة"}</small></span></div>}
          {!attempting && !upload?.busy && (upload?.errorCode || localUploadError) && <UploadFailure
            code={upload?.errorCode ?? localUploadError ?? "STATEMENT_IMPORT_REQUEST_FAILED"} />}
          <button className="portfolio-x-primary-action" type="submit"
            disabled={attempting || upload?.busy || upload?.connection !== "READY"}>
            {attempting || upload?.busy ? <><LoaderCircle />{upload?.busyLabel ?? "جارٍ بدء الفحص"}</>
              : <><FileSearch />افحص الحزمة واعرض المراجعة</>}</button></form>}
        {upload?.connection === "UNAVAILABLE" && <p className="portfolio-next-import-error" role="alert">اتصال الاستيراد غير متاح لهذا الدفتر.</p>}
        {files.length === 0 && upload?.connection === "READY" && upload.errorCode
          && <UploadFailure code={upload.errorCode} />}
      </section>
      <section className="portfolio-x-panel portfolio-x-import-review">
        <PanelHead title={staged.length ? "مراجعة الحزمة قبل الاعتماد" : "حالة الدفتر الحالية"}
          subtitle={staged.length ? `${staged.length} ملفات قرأها الخادم` : model ? qualityLabel(model.dataQuality.state) : "لا توجد حزمة مقبولة"} />
        {staged.length ? <div className="portfolio-next-staged-review"><div className="portfolio-next-source-files">{staged.map((file) => <article key={file.importId}>
          <b>{file.originalName}</b><small>{file.role} · {file.recordCount} سجلًا · {file.actionableRecords} قابلًا للتسكين</small>
          <code>{file.exceptionFields ? `${file.exceptionFields} حقول مستبعدة` : file.state}</code>
        </article>)}</div><div className="portfolio-next-review-ack"><ShieldCheck /><p>الاعتماد يثبت الملاحظات في دفتر <b>{bookKind === "PAPER" ? "Paper" : "المحفظة الحقيقية"}</b> فقط. لا ينشئ أوامر ولا يغيّر سجلًا سابقًا.</p></div>
          <button className="portfolio-x-primary-action" type="button" disabled={!upload?.canApprove || upload.busy}
            onClick={() => void upload?.onApprove()}>{upload?.busy ? <><LoaderCircle />{upload.busyLabel ?? "جارٍ الاعتماد"}</> : <><CheckCircle2 />اعتماد الحزمة وتحديث الشاشات</>}</button></div>
          : model ? <div className="portfolio-next-source-files">{model.dataQuality.files.map((file) => <article key={file.fileId}>
          <b>{file.originalName}</b><small>{file.role} · {file.recordCount} سجلًا</small><code>{file.sha256.slice(0, 16)}…</code>
        </article>)}</div> : <div className="portfolio-x-review-placeholder"><FileSearch /><b>المراجعة ستظهر هنا</b>
          <p>الأدوار، عدد السجلات، الرسوم المفقودة، التعارضات، وأثر كل مصدر.</p></div>}
      </section>
    </div>
  </div>;
}

function UploadFailure({ code }: { code: string }) {
  return <div className="portfolio-next-upload-feedback is-error" role="alert" aria-live="assertive">
    <AlertTriangle /><span><b>{statementUploadErrorMessage(code)}</b><code>{code}</code></span></div>;
}

export function statementUploadErrorMessage(code: string): string {
  if (code === "STATEMENT_IMPORT_PENDING_OPERATION_INPUT_MISMATCH") {
    return "تعذر حسم محاولة رفع سابقة بأمان. حدّث الصفحة ثم أعد فحص الحزمة الحالية.";
  }
  if (code === "STATEMENT_IMPORT_PENDING_OPERATION_PENDING") {
    return "العملية السابقة ما زالت قيد المعالجة على الخادم؛ لم نكرر إرسالها حفاظًا على الدفتر.";
  }
  if (["ADMIN_SESSION_REQUIRED", "ADMIN_SESSION_EXPIRED", "ADMIN_STEP_UP_NOT_FRESH"].includes(code)) {
    return "انتهت جلسة الإدارة؛ افتحها مجددًا ثم أعد الفحص مع الاحتفاظ بالملفات المختارة.";
  }
  if (code === "STATEMENT_IMPORT_FILE_COUNT_INVALID") return "اختر ملفًا واحدًا على الأقل لبدء الفحص.";
  if (code === "STATEMENT_IMPORT_FILE_EMPTY") return "أحد الملفات المختارة فارغ ولا يمكن فحصه.";
  if (code === "STATEMENT_IMPORT_FILE_TOO_LARGE") return "أحد الملفات يتجاوز الحد المسموح للرفع.";
  if (code === "STATEMENT_IMPORT_FORMAT_UNSUPPORTED") return "أحد الملفات ليس بصيغة CSV مدعومة في هذا الإصدار.";
  if (["STATEMENT_IMPORT_SECURE_HASH_UNAVAILABLE", "STATEMENT_IMPORT_SECURE_OPERATION_ID_UNAVAILABLE"].includes(code)) {
    return "تعذر تشغيل التحقق الآمن للملفات في هذا المتصفح.";
  }
  if (code === "STATEMENT_IMPORT_SCOPE_CHANGED") return "تغيّر الدفتر أثناء الرفع؛ لم تُسند النتائج إلى دفتر آخر.";
  if (code === "STATEMENT_IMPORT_EXACT_OPERATION_RETRY_FAILED") return "تعذر حسم إعادة المحاولة المطابقة للملف الحالي.";
  if (code === "STATEMENT_IMPORT_CONNECTION_UNAVAILABLE") return "اتصال الاستيراد غير متاح لهذا الدفتر حاليًا.";
  if (code === "STATEMENT_IMPORT_OUTCOME_UNKNOWN") return "انقطع الاتصال أثناء الرفع؛ جارٍ التحقق من نتيجة العملية قبل السماح بإعادتها.";
  if (code === "MY_PORTFOLIO_BUNDLE_REQUIRES_FIVE_FILES") return "اختر ملفات TradingView الخمسة معًا قبل اعتماد الحزمة.";
  if (["MY_PORTFOLIO_BUNDLE_ROLE_INVALID", "MY_PORTFOLIO_BUNDLE_ROLE_SET_INVALID"].includes(code)) {
    return "الحزمة لا تحتوي على الأدوار الخمسة المطلوبة مرة واحدة: المراكز، الرصيد، سجل الأوامر، الأوامر الحالية، والنشاط.";
  }
  if (code === "MY_PORTFOLIO_BUNDLE_IMPORT_UNUSABLE") return "أحد ملفات الحزمة لم ينجح فحصه ولا يمكن اعتماده.";
  if (code === "MY_PORTFOLIO_BUNDLE_IMPORT_NOT_FOUND") return "تعذر العثور على أحد الملفات المرفوعة داخل هذا الدفتر.";
  return "تعذر بدء فحص الحزمة. احتفظ بالرمز التالي لتحديد السبب بدقة.";
}

function EmptyScreen({ onUpload }: { onUpload(): void }) {
  return <div className="portfolio-x-empty-screen"><span className="portfolio-x-empty-art"><WalletCards /></span>
    <small className="portfolio-x-empty-code">MY PORTFOLIO NEXT · CLEAN BOOK</small><h2>لا توجد حقيقة مالية معتمدة لهذا الدفتر</h2>
    <p>ابدأ برفع حزمة البروكر. ستظل كل القيم فارغة حتى تنتهي القراءة والمراجعة والتسكين الحتمي.</p>
    <button type="button" onClick={onUpload}><FileUp />رفع كشف البروكر</button>
    <div className="portfolio-x-empty-rules"><span><CheckCircle2 />لا أرقام وهمية</span><span><CheckCircle2 />لا خلط بين الدفاتر</span></div></div>;
}
